/**
 * Hosting module.
 *
 * Owns hosts and managed mode (see wiki/decisions/2026-10-09-introduce-hosts-and-managed-rooms.md):
 * - only the owner designates hosts, switches managed mode, or names the host role;
 * - the owner's newly joining human mates become hosts by default, and in Omni
 *   mode the first Omni mate stands in for a host;
 * - in managed mode only a client with a host mate may start or end games;
 * - the Host plays setting decides which mates are eligible for player seats.
 */

import type { RoomStorage, BroadcastChannel, InternalRoomState } from '../../types/infrastructure.types'
import type { RoomCaller } from '../../types/room.types'
import type { Mate, MateInRoom } from '../../types/types'
import type { SetHostArgs, SetHostTermArgs, SetManagedModeArgs } from '../../schemas/action.schemas'

/** What the room calls a host unless the owner names the role, such as "Teacher". */
export const DEFAULT_HOST_TERM = 'Host'

export const HOST_PLAYS_OPTION_ID = 'hostPlays'
export type HostPlays = 'ifUnmanaged' | 'always' | 'never'

/** Platform-provided game option shown alongside every game's own options. */
export const HOST_PLAYS_OPTION = {
  id: HOST_PLAYS_OPTION_ID,
  type: 'enum' as const,
  label: 'Host plays',
  description: 'Whether hosts take player seats or observe.',
  scope: 'global' as const,
  choices: [
    { value: 'ifUnmanaged', label: 'If unmanaged' },
    { value: 'always', label: 'Always' },
    { value: 'never', label: 'Never' },
  ],
  defaultValue: 'ifUnmanaged',
}

export const PLATFORM_GAME_OPTIONS = [HOST_PLAYS_OPTION]

export interface HostingContainer {
  storage: RoomStorage
  broadcastChannel: BroadcastChannel
}

/** A mate that can hold host status: anyone but a bot. */
function canHost(mate: MateInRoom): boolean {
  return !mate.isAI
}

/**
 * A client's mates who are hosts by default when the owner joins with them:
 * its human mates, and in Omni mode the first Omni mate, so that one fake
 * mate coordinates while the others play.
 */
function defaultHostMates(mates: MateInRoom[]): MateInRoom[] {
  const humans = mates.filter((m) => !m.isAI && !m.isOmniMate)
  return [...humans, ...mates.filter((m) => m.isOmniMate).slice(0, 1)]
}

export function hostMateIdsOf(state: InternalRoomState): string[] {
  return state.hostMateIds ?? []
}

export function isHostMate(state: InternalRoomState, mateId: string): boolean {
  return hostMateIdsOf(state).includes(mateId)
}

/** Whether the calling client represents at least one host mate. */
export function callerIsHost(state: InternalRoomState, caller: RoomCaller | undefined): boolean {
  const clientId = caller?.clientId
  if (!clientId) return false
  return state.mates.some((m) => m.clientId === clientId && isHostMate(state, m.mateId))
}

/**
 * Returns an error when the caller may not start or end games. Internal calls
 * (no caller) and unmanaged rooms are always allowed.
 */
export function lifecycleAuthorityError(state: InternalRoomState, caller: RoomCaller | undefined): string | null {
  if (!caller || !state.managed) return null
  return callerIsHost(state, caller) ? null : 'Only hosts can start or end games in a managed room'
}

export function normalizeHostPlays(value: unknown): HostPlays {
  return value === 'always' || value === 'never' ? value : 'ifUnmanaged'
}

/** Whether hosts are seated for the room's current mode under a Host plays setting. */
export function hostsTakeSeats(hostPlays: HostPlays, managed: boolean): boolean {
  if (hostPlays === 'always') return true
  if (hostPlays === 'never') return false
  return !managed
}

/** The parts of a room that decide who may take a seat, as clients also see them. */
export interface SeatingRoom<M extends Mate> {
  mates: M[]
  hostMateIds?: string[]
  managed?: boolean
}

/**
 * Mates eligible for player seats: everyone except hosts who sit out under
 * Host plays, and, while any client is in Omni mode, real human mates (see
 * Mate.isOmniMate), so Omni mates and bots fill the seats. Clients use this
 * too, so a game tile agrees with whether the room will start the game.
 */
export function seatEligibleMates<M extends Mate>(room: SeatingRoom<M>, hostPlays: HostPlays): M[] {
  const omni = room.mates.some((m) => m.isOmniMate)
  const hostsSeated = hostsTakeSeats(hostPlays, Boolean(room.managed))
  const hostIds = room.hostMateIds ?? []
  return room.mates.filter((m) => {
    if (omni && !m.isOmniMate && !m.isAI) return false
    return hostsSeated || !hostIds.includes(m.mateId)
  })
}

/** Each game's Host plays setting where the owner saved one; other games use the default. */
export function hostPlaysByGameIdOf(state: InternalRoomState): Record<string, HostPlays> {
  const result: Record<string, HostPlays> = {}
  for (const [gameId, saved] of Object.entries(state.savedGameOptionsById ?? {})) {
    const value = saved?.global?.[HOST_PLAYS_OPTION_ID]
    if (value !== undefined) result[gameId] = normalizeHostPlays(value)
  }
  return result
}

/**
 * Moves mates who may no longer take a seat (such as hosts after Host plays
 * or managed mode changed) from their seats to the observers. Returns null
 * when that would leave a seat with nobody in it.
 */
export function unseatMates(
  seats: { mateGroups: string[][]; observerIDs: string[] },
  unseatedIds: Set<string>,
): { mateGroups: string[][]; observerIDs: string[] } | null {
  const mateGroups = seats.mateGroups.map((group) => group.filter((id) => !unseatedIds.has(id)))
  if (mateGroups.some((group) => group.length === 0)) return null
  const unseated = seats.mateGroups.flat().filter((id) => unseatedIds.has(id))
  return { mateGroups, observerIDs: [...seats.observerIDs, ...unseated] }
}

export function makeHosting(container: HostingContainer) {
  const { storage, broadcastChannel } = container

  const commit = (state: InternalRoomState, hostMateIds: string[], extra: Partial<InternalRoomState> = {}) => {
    const next = { ...state, ...extra, hostMateIds }
    storage.setState(next)
    broadcastChannel.broadcast({
      type: 'hostingChanged',
      data: { hostMateIds, managed: Boolean(next.managed), hostTerm: next.hostTerm ?? DEFAULT_HOST_TERM },
    })
  }

  const setHost = async (args: SetHostArgs, caller?: RoomCaller): Promise<{ success: boolean; error?: string }> => {
    if (!caller?.isOwner) return { success: false, error: 'Only the room owner can change hosts' }
    const state = storage.getState()
    const mate = state.mates.find((m) => m.mateId === args.mateId)
    const current = hostMateIdsOf(state)

    if (args.isHost) {
      if (!mate) return { success: false, error: 'Mate not found' }
      if (!canHost(mate)) return { success: false, error: 'Bots cannot be hosts' }
      if (current.includes(mate.mateId)) return { success: true }
      const ownMate = mate.clientId === caller.clientId
      commit(state, [...current, mate.mateId], ownMate ? { ownerMatesAreHosts: true } : {})
      return { success: true }
    }

    if (!current.includes(args.mateId)) return { success: true }
    const remaining = current.filter((id) => id !== args.mateId)
    const ownMate = mate?.clientId === caller.clientId
    commit(state, remaining, ownMate ? { ownerMatesAreHosts: false } : {})
    return { success: true }
  }

  const setManagedMode = async (args: SetManagedModeArgs, caller?: RoomCaller): Promise<{ success: boolean; error?: string }> => {
    if (!caller?.isOwner) return { success: false, error: 'Only the room owner can change managed mode' }
    const state = storage.getState()
    commit(state, hostMateIdsOf(state), { managed: args.managed })
    return { success: true }
  }

  /** Names the host role everywhere the room shows it, such as "Teacher" or "Facilitator". */
  const setHostTerm = async (args: SetHostTermArgs, caller?: RoomCaller): Promise<{ success: boolean; error?: string }> => {
    if (!caller?.isOwner) return { success: false, error: 'Only the room owner can rename hosts' }
    const state = storage.getState()
    commit(state, hostMateIdsOf(state), { hostTerm: args.hostTerm })
    return { success: true }
  }

  /**
   * Keeps host status in step with the roster after a client updates its
   * mates: departed mates lose host status, and while the owner has not opted
   * out, the owner's client's default host mates are hosts.
   */
  const reconcileAfterMatesUpdate = (clientId: string, caller?: RoomCaller) => {
    const state = storage.getState()
    const present = new Set(state.mates.map((m) => m.mateId))
    const current = hostMateIdsOf(state)
    const next = current.filter((id) => present.has(id))

    const ownerClient = caller?.isOwner && caller.clientId === clientId
    if (ownerClient && state.ownerMatesAreHosts !== false) {
      for (const mate of defaultHostMates(state.mates.filter((m) => m.clientId === clientId))) {
        if (!next.includes(mate.mateId)) next.push(mate.mateId)
      }
    }

    const changed = next.length !== current.length || next.some((id, i) => id !== current[i])
    if (changed) commit(state, next)
  }

  return { setHost, setManagedMode, setHostTerm, reconcileAfterMatesUpdate }
}

export type HostingModule = ReturnType<typeof makeHosting>
